import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { LocalStorageService } from '../shared/services/local-storage.service';
import { catchError, filter, map, startWith, switchMap } from 'rxjs/operators';
import { Subscription, throwError } from 'rxjs';
import { HttpService } from '../shared/services/http.service';
import { NavigationEnd, Router } from '@angular/router';
import { HeaderService } from '../shared/services/header.service';
import { ICredential } from '../credentials/interfaces/credential.interface';
import {
  BackgroundCheckStatus,
  IUser,
} from '../authentication/interfaces/user.interface';
import { PopupService } from '../shared/services/popup.service';
import { UploadedCredentials } from '../credentials/interfaces/employeeCredential.interface';
import { Platform } from '@ionic/angular';
import {
  IRole,
  IRoleCredentialSetting,
} from '../shared/interfaces/role.interface';
import { SideMenuService } from '../shared/services/side-menu.service';
import { HttpErrorResponse } from '@angular/common/http';
import { IReference } from '../credentials/interfaces/reference.interface';
import { HelperService } from '../shared/services/helper.service';
import { SharedDataService } from '../shared/services/shared-data.service';

type CredentialGroupStatuses = {
  [key: string]: 'pending' | 'approved' | 'rejected' | 'expired';
};

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  standalone: false
})
export class HomeComponent implements OnInit {
  user?: IUser;
  roleName?: string;
  moreRoles?: string;
  location?: string;
  criticalCredentials: ICredential[] = [];
  nonCriticalCredentials: ICredential[] = [];
  approvedCredentials: string[] = [];
  status = 'pending';
  uploadedCredentials: UploadedCredentials = {};
  uploadedCredentialStatuses: CredentialGroupStatuses = {};
  router$?: Subscription;
  isReferenceRequired = false;
  referencesStatus = 'missing';
  appResumer$?: Subscription;
  referencesSubmited = false;

  backgroundCheckStatus: BackgroundCheckStatus =
    BackgroundCheckStatus.NOT_REQUIRED;

  identityVerificationCheckStatus: BackgroundCheckStatus =
    BackgroundCheckStatus.NOT_REQUIRED; // <-- NEW PROPERTY

  isLoading: boolean = false;
  isSubmittingForApproval: boolean = false;
  disableSubmitingForApproval = true;
  submitingForApprovalBtnText = 'Submit for approval';

  constructor(
    private localStorageService: LocalStorageService,
    private httpService: HttpService,
    private router: Router,
    private headerService: HeaderService,
    private popupService: PopupService,
    public platform: Platform,
    private sideMenuService: SideMenuService,
    private helperService: HelperService,
    private sharedDataService: SharedDataService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.router$ = this.router.events
      .pipe(
        filter(
          (event) => event instanceof NavigationEnd && event.url === '/home'
        ),
        startWith({ url: this.router.url }),
        switchMap(() => {
          this.isLoading = true;
          return this.fetchUser();
        }),
        switchMap((user) => {
          this.user = user;
          return this.fetchReferences(user);
        }),
        switchMap((references) => {
          if (references?.length >= 3) {
            this.referencesSubmited = true;
            this.referencesStatus = !this.referencesSubmited
              ? 'missing'
              : references.every((ref) => ref.status === 'approved')
              ? 'approved'
              : references.some((ref) => ref.status === 'rejected')
              ? 'rejected'
              : 'pending';
          }
          return this.fetchEmployeeCredentials();
        }),
        map((employeeCreds) => ({ employeeCreds, user: this.user })),
        catchError((error: HttpErrorResponse) => {
          this.isLoading = false;
          console.log('error while fetching uploaded credentials ... ', error);
          if (error.error.statusCode === 401) {
            this.sideMenuService.logout();
          }
          return throwError(() => error);
        })
      )
      .subscribe(({ employeeCreds: uploadedCredentials, user }) => {
        if (user) {
          this.setUserData(user);
          this.mapUploadedCredentials(uploadedCredentials);
          this.isLoading = false;
          this.showWelcomePopup(user);

          // if user required to perform identity verification, then navigate to identity verification screen
          if (
            user?.identityVerificationCheckStatus ===
            BackgroundCheckStatus.REQUIRED
          ) {
            this.router.navigateByUrl('/identity-verification', {
              replaceUrl: true,
            });
          }

          // Trigger change detection to avoid ExpressionChangedAfterItHasBeenCheckedError
          this.cdr.detectChanges();
        }
      });

    // Refresh app on coming to foreground from background
    this.appResumer$ = this.platform.resume.subscribe(() => {
      this.doRefresh();
    });
  }

  async fetchEmployeeCredentials(): Promise<UploadedCredentials> {
    return this.httpService.httpGetRequest<UploadedCredentials>(
      '/employee-credentials'
    );
  }

  async fetchReferences(user: IUser): Promise<IReference[]> {
    return this.httpService.httpGetRequest<IReference[]>(
      `/references/${user.id}`
    );
  }

  async fetchUser(): Promise<IUser> {
    return this.httpService.httpGetRequest<IUser>('/auth/employee/me');
  }

  mapUploadedCredentials(uploadedCredentials: UploadedCredentials): void {
    this.uploadedCredentials = uploadedCredentials;

    this.setSubmitForApprovalBtnVisibility();

    this.uploadedCredentialStatuses = this.getCredentialGroupStatus(
      this.uploadedCredentials
    );

    this.approvedCredentials = Object.entries(this.uploadedCredentialStatuses)
      .filter(([cred, status]) => status === 'approved')
      .map(([cred]) => cred);

    const urgentCredentialsCount = this.criticalCredentials.filter((c) => {
      const credStatus = this.uploadedCredentialStatuses[c.name];
      return (
        !credStatus || credStatus === 'rejected' || credStatus === 'expired'
      );
    }).length;
    this.headerService.urgentCredentialsCount.next(urgentCredentialsCount);
  }

  ngOnDestroy(): void {
    this.router$?.unsubscribe();
    this.appResumer$?.unsubscribe();
  }

  goToReferencesScreen() {
    this.router.navigateByUrl('/credentials/references', {
      state: {},
    });
  }

  getCredentialGroupStatus(
    uploadedCredentials: UploadedCredentials
  ): CredentialGroupStatuses {
    return Object.entries(uploadedCredentials).reduce(
      (acc: CredentialGroupStatuses, [credName, credArray]) => {
        if (credArray.every((c) => c.status === 'pending')) {
          acc[credName] = 'pending';
        } else if (credArray.some((c) => c.status === 'approved')) {
          acc[credName] = 'approved';
        } else {
          // Check if latest uploaded credential is in pending state
          const certificateRecentlyUploaded = credArray.sort(
            (credA, credB) =>
              Date.parse(credB.createdAt || '') -
              Date.parse(credA.createdAt || '')
          )[0];
          acc[credName] = certificateRecentlyUploaded.status;

          // Check if max expiry certificate is expired
          const certificateWithMaxExpiry = credArray.sort(
            (credA, credB) =>
              Date.parse(credB.expiryDate || '') -
              Date.parse(credA.expiryDate || '')
          )[0];
          if (
            Date.parse(certificateWithMaxExpiry.expiryDate || '') < Date.now()
          ) {
            acc[credName] = 'expired';
          }
        }

        return acc;
      },
      {}
    );
  }

  async submitCredentials() {
    this.isSubmittingForApproval = true;
    try {

      let showRCMPText = false;
      if (
        this.user?.backgroundCheckStatus === BackgroundCheckStatus.REQUIRED ||
        this.user?.backgroundCheckStatus === BackgroundCheckStatus.PENDING
      ) {
        showRCMPText = true;
      }

      await this.httpService.httpPutRequest(
        '/employees/submit-for-approval',
        {}
      );
      const user = await this.fetchUser();
      await this.setUserData(user);
      this.setSubmitForApprovalBtnVisibility();
      // if rcmp background check is required, then inform user to check email for background check instructions
      
        
      this.popupService.showSubmitForReviewModal({showRCMPText});
    } catch (err: any) {
      console.log(err.error.message);
      if (err.error.message) {
        this.popupService.showErrorModal(err.error.message);
      }
    } finally {
      this.isSubmittingForApproval = false;
    }
  }

  async doRefresh(event?: any): Promise<void> {
    try {
      const employeeCredentials = await this.fetchEmployeeCredentials();
      this.headerService.showActionRequiredCTA();
      this.mapUploadedCredentials(employeeCredentials);
    } catch (error) {
      console.log(error);
    } finally {
      if (event) {
        event.target.complete();
      }
    }
  }

  async setUserData(user: IUser) {
    this.user = user;
    // Set user data in shared service for other components to use
    this.sharedDataService.currentUser = user;
    
    if (!user.roles?.length) {
      this.isLoading = false;
      return;
    }
    this.roleName =
      this.helperService.transformRoleNameToCapitalize(user.roles[0]?.name) ||
      'Unknown';
    this.moreRoles =
      user.roles.length > 1 ? ` and ${user.roles?.length - 1} more` : '';

    const roleCredentialSettingsMap: {
      [credentialId: number]: IRoleCredentialSetting;
    } = {};
    for (const role of user.roles) {
      for (const item of role.roleCredentialSettings) {
        if (!roleCredentialSettingsMap[item.credentialId]) {
          roleCredentialSettingsMap[item.credentialId] = item;
        } else if (
          !roleCredentialSettingsMap[item.credentialId].isCredentialCritical &&
          item.isCredentialCritical
        ) {
          roleCredentialSettingsMap[item.credentialId].isCredentialCritical =
            true;
        }
      }
    }

    this.criticalCredentials = [];
    this.nonCriticalCredentials = [];
    for (const credentialId in roleCredentialSettingsMap) {
      if (roleCredentialSettingsMap[credentialId].isCredentialCritical) {
        this.criticalCredentials.push(
          roleCredentialSettingsMap[credentialId].credential
        );
      } else {
        this.nonCriticalCredentials.push(
          roleCredentialSettingsMap[credentialId].credential
        );
      }
    }

    this.isReferenceRequired = user.roles.some(
      (role: IRole) => role.referencesRequired
    );
    this.location = user.location?.street || 'Unknown';
    this.backgroundCheckStatus = user.backgroundCheckStatus;
    this.identityVerificationCheckStatus = user.identityVerificationCheckStatus; // <-- SET HERE
    this.localStorageService.setLoggedInUserInfo(user);
  }

  showNoCriticalCreadentialRequired() {
    if (this.backgroundCheckStatus === BackgroundCheckStatus.REQUIRED) {
      return false;
    }
    if (this.isReferenceRequired) {
      return false;
    }
    for (const item of this.criticalCredentials) {
      if (this.uploadedCredentialStatuses[item.name] !== 'approved') {
        return false;
      }
    }
    return true;
  }

  setSubmitForApprovalBtnVisibility(): void {
    const isCriticalCredsUploaded =
      this.criticalCredentials.every((cred) => {
        return this.uploadedCredentials[cred.name];
      }) && (this.isReferenceRequired ? this.referencesSubmited : true);
    const isAllCredsUploaded =
      this.nonCriticalCredentials.every((cred) => {
        return this.uploadedCredentials[cred.name];
      }) && isCriticalCredsUploaded;

    this.submitingForApprovalBtnText =
      isAllCredsUploaded && !!this.user?.submitForApproval
        ? this.user.status === 'compliant' ? 'Compliant' : 'Waiting for approval'
        : 'Submit for approval';
    this.disableSubmitingForApproval =
      !isCriticalCredsUploaded || !!this.user?.submitForApproval;
  }

  noNonCriticalCredRequired(): boolean {
    if (!this.nonCriticalCredentials?.length) {
      return false;
    }

    for (const item of this.nonCriticalCredentials) {
      if (this.uploadedCredentialStatuses[item.name] !== 'approved') {
        return true;
      }
    }

    return false;
  }

  async showWelcomePopup(user: IUser) {
    if (user.intro?.welcome) return;

    await this.popupService.showWelcomeModal(user);
    const intro = user.intro || {};
    this.httpService.httpPutRequest(`/auth/employee/profile`, {
      intro: {
        ...intro,
        welcome: true,
      },
    });
  }
}
